import React, { useEffect, useRef } from 'react';
import Player from '@vimeo/player';
import { parseVimeoUrl } from '../utils/vimeoUrl';
import {
  readLocalVideoPosition,
  writeLocalVideoPosition,
  resolveResumeSeconds
} from '../utils/videoPositionStorage';
import { updateLessonVideoPosition } from '../services/progressService';

/** Throttle localStorage writes from Vimeo timeupdate events. */
const LOCAL_THROTTLE_MS = 1000;

/**
 * True when the Vimeo SDK calls ready() on a player that destroy() already
 * removed from its internal map. That rejection is uncaught inside the SDK
 * (appendVideoMetadata has no .catch) and CRA shows a fullscreen overlay.
 */
const isUnknownPlayerError = (error) =>
  Boolean(error && String(error.message || error).includes('Unknown player'));

/**
 * Vimeo lesson player that restores playback position after the student
 * navigates away. Position is kept in localStorage while watching and
 * flushed to user_lessons on pause, tab hide, and unmount.
 *
 * React owns an empty container. The effect creates the iframe and Player
 * so destroy() can remove that iframe without emptying React's tree
 * (which caused a black box under Strict Mode). The iframe is created with
 * src set — we do not use the SDK oEmbed `url` option, whose async callback
 * can re-register a destroyed player and throw "Unknown player. Probably unloaded."
 *
 * Does not autoplay on restore (browser autoplay policy and accessibility).
 */
const LessonVideoPlayer = ({
  videoLink,
  title,
  userId,
  lessonId,
  firestorePositionSeconds,
  firestoreDurationSeconds,
  firestorePositionUpdatedAt
}) => {
  const containerRef = useRef(null);
  const latestSecondsRef = useRef(0);
  const latestDurationRef = useRef(0);
  const lastLocalWriteRef = useRef(0);
  const lastFlushedSecondsRef = useRef(-1);
  const canFlushRef = useRef(false);
  const firestoreRef = useRef({
    seconds: firestorePositionSeconds,
    duration: firestoreDurationSeconds,
    updatedAt: firestorePositionUpdatedAt
  });

  // Keep Firestore snapshot current without re-creating the player.
  firestoreRef.current = {
    seconds: firestorePositionSeconds,
    duration: firestoreDurationSeconds,
    updatedAt: firestorePositionUpdatedAt
  };

  const parsed = parseVimeoUrl(videoLink);

  useEffect(() => {
    const parsedLink = parseVimeoUrl(videoLink);
    const container = containerRef.current;
    if (!parsedLink || !container || !userId || !lessonId) return undefined;

    const iframe = document.createElement('iframe');
    iframe.src = parsedLink.embedUrl;
    iframe.title = title || 'Lesson video';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.setAttribute('allowFullScreen', '');
    iframe.setAttribute('frameBorder', '0');
    iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;';
    container.appendChild(iframe);

    const player = new Player(iframe);
    let cancelled = false;
    canFlushRef.current = false;
    lastFlushedSecondsRef.current = -1;
    lastLocalWriteRef.current = 0;

    /**
     * Swallow the SDK's uncaught "Unknown player" rejection so returning to
     * the lesson does not show the CRA error overlay. Only this message is ignored.
     */
    const onUnhandledRejection = (event) => {
      if (isUnknownPlayerError(event.reason)) {
        event.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection, true);

    /**
     * Write localStorage immediately and merge position to Firestore.
     * Skips Firestore until resume has been applied, and skips duplicate seconds.
     */
    const flush = (resetToStart = false) => {
      if (!canFlushRef.current) return;

      const seconds = resetToStart ? 0 : latestSecondsRef.current;
      const duration = latestDurationRef.current;
      latestSecondsRef.current = seconds;
      writeLocalVideoPosition(userId, lessonId, seconds, duration);

      const rounded = Math.floor(seconds);
      if (rounded === lastFlushedSecondsRef.current) return;
      lastFlushedSecondsRef.current = rounded;

      updateLessonVideoPosition(userId, lessonId, { seconds, duration }).catch((error) => {
        console.error('Error saving video position:', error);
      });
    };

    const applyResume = async () => {
      if (cancelled) return;

      let duration = 0;
      try {
        duration = await player.getDuration();
      } catch (error) {
        if (cancelled || isUnknownPlayerError(error)) return;
        duration = firestoreRef.current.duration || 0;
      }
      if (cancelled) return;
      latestDurationRef.current = duration;

      const start = resolveResumeSeconds({
        local: readLocalVideoPosition(userId, lessonId),
        firestoreSeconds: firestoreRef.current.seconds,
        firestoreUpdatedAt: firestoreRef.current.updatedAt,
        duration
      });

      if (start > 0) {
        try {
          await player.setCurrentTime(start);
        } catch (error) {
          if (!cancelled && !isUnknownPlayerError(error)) {
            console.error('Error restoring video position:', error);
          }
        }
      }
      if (cancelled) return;
      latestSecondsRef.current = start;
      canFlushRef.current = true;
    };

    player.ready().then(() => {
      if (!cancelled) {
        return applyResume();
      }
      return undefined;
    }).catch((error) => {
      if (!cancelled && !isUnknownPlayerError(error)) {
        console.error('Error initializing Vimeo player:', error);
      }
    });

    player.on('timeupdate', ({ seconds, duration }) => {
      if (cancelled) return;
      latestSecondsRef.current = seconds;
      if (typeof duration === 'number' && duration > 0) {
        latestDurationRef.current = duration;
      }
      canFlushRef.current = true;
      const now = Date.now();
      if (now - lastLocalWriteRef.current >= LOCAL_THROTTLE_MS) {
        lastLocalWriteRef.current = now;
        writeLocalVideoPosition(userId, lessonId, seconds, latestDurationRef.current);
      }
    });

    player.on('pause', () => {
      if (!cancelled) flush(false);
    });

    // Ended videos should restart at 0 on the next visit.
    player.on('ended', () => {
      if (!cancelled) flush(true);
    });

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flush(false);
      }
    };
    const onPageHide = () => {
      flush(false);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      cancelled = true;
      flush(false);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      // Do not player.off() here — off() calls callMethod, which races destroy()
      // and is the source of "Unknown player. Probably unloaded."
      player.destroy().catch(() => {}).finally(() => {
        // SDK rejections can land in a microtask after destroy(); keep the handler briefly.
        window.setTimeout(() => {
          window.removeEventListener('unhandledrejection', onUnhandledRejection, true);
        }, 0);
      });
    };
  }, [videoLink, userId, lessonId, title]);

  if (!parsed) return null;

  return (
    <div className="relative w-full bg-black rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
      {/* SDK iframe is appended here in the effect so destroy() cannot orphan React's tree. */}
      <div
        ref={containerRef}
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );
};

export default LessonVideoPlayer;
